'use client'

import classNames from 'classnames'
import { useSiteContext } from '@dustin-web/microsite-components/src/hooks'
import React, { useEffect, useRef, useState } from 'react'
import { ProductTypeProps } from '.'
import AddToCartButton from '../add-to-cart-button'

import { DigitalDataOrigin, ProductWithCategoryType } from '../utils/digital-data-types'
import { useTrackingOnScreen } from '@dustin-web/microsite-components/src/on-screen/'
import { Card } from './product-card-gallery-composable'
import { ProductDigitalData } from '../utils/product-digital-data'
import { getProductLabels } from './utils'
import type { Props as HeadingProps } from '@dustin-web/microsite-components/src/heading'
import { getRefVar, lastIndexVar } from '@dustin-web/microsite-components/src/hooks/vars'

type Props = {
  imagePriority?: boolean
  product?: ProductTypeProps | null
  ticket: string
  digitalDataOrigin: DigitalDataOrigin
  digitalIndex?: number
  showAddToCartButton?: boolean
  sizeClasses?: string
  hTag?: HeadingProps['hTag']
}

const ProductCardCompact = ({
  product,
  ticket,
  imagePriority = false,
  digitalDataOrigin,
  digitalIndex = 0,
  showAddToCartButton = false,
  sizeClasses,
  hTag,
}: Props) => {
  const [showAddTo, setShowAddTo] = useState(false)

  if (!product) {
    return null
  }

  return (
    <Container
      setShowAddTo={setShowAddTo}
      sizeClasses={sizeClasses}
      digitalIndex={digitalIndex}
      product={product}
      digitalDataOrigin={digitalDataOrigin}>
      <Card.Picture
        className="u-mr-12"
        product={product}
        ticket={ticket}
        imageWidth={64}
        imageHeight={48}
        imagePriority={imagePriority}
        digitalDataIndex={digitalIndex}
        digitalDataOrigin={digitalDataOrigin}
      />
      <div
        className={`c-product-card__product-info ${
          product.availability?.isAvailableForSale ? 'u-mr-12' : ''
        }`}>
        <div className="d-flex flex-column flex-fill u-w-100">
          <div className="flex-fill">
            <Card.Title
              product={product}
              ticket={ticket}
              digitalDataIndex={digitalIndex}
              digitalDataOrigin={digitalDataOrigin}
              hTag={hTag}
              compact
              ellipsis
            />
          </div>
        </div>
        <div className="col-3"></div>
      </div>
      {product.availability?.isAvailableForSale && (
        <>
          {!showAddToCartButton ? (
            <div>
              <div className="d-flex flex-column">
                <div className="flex-fill">
                  <Card.Price
                    price={product.price}
                    isAvailableForSale={product.availability?.isAvailableForSale}
                    displayVatInfo={false}
                  />
                </div>
              </div>
            </div>
          ) : (
            <Actions
              showAddTo={showAddTo}
              product={product}
              ticket={ticket}
              digitalDataOrigin={digitalDataOrigin}
              digitalIndex={digitalIndex}
              showAddToCartButton={showAddToCartButton}
            />
          )}
        </>
      )}
    </Container>
  )
}

type ActionsProps = {
  showAddTo: boolean
  product: Props['product']
  ticket: Props['ticket']
  digitalDataOrigin: Props['digitalDataOrigin']
  digitalIndex: Props['digitalIndex']
  showAddToCartButton: Props['showAddToCartButton']
}
const Actions = ({
  showAddTo,
  product,
  ticket,
  digitalDataOrigin,
  digitalIndex,
  showAddToCartButton,
}: ActionsProps) => {
  const { isMobile } = useSiteContext()

  if (isMobile || !product) {
    return null
  }
  return (
    <div
      className="u-absolute d-flex align-items-center u-pl-16"
      style={{
        background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 9%)',
        top: 2,
        right: 16,
      }}>
      <div className="d-flex flex-column">
        <div className="flex-fill">
          <Card.Price
            price={product.price}
            isAvailableForSale={product.availability?.isAvailableForSale}
            displayVatInfo={false}
          />
        </div>
      </div>
      <div
        className={classNames('u-overflow-hidden u-pt-4', {
          'u-w-64': showAddTo,
          'u-w-0': !showAddTo,
        })}
        style={{ transition: 'all 300ms ease' }}>
        <div className="u-pl-16">
          <div>
            <div className="d-flex justify-content-end">
              <Card.Availability
                availability={product.availability}
                isBid={product.price?.isBid}
                showOnlyIcon
                className="u-pb-4"
              />
            </div>
          </div>
          <AddToCartButton
            mainProductErpIdentifier={null}
            product={product}
            ticket={ticket}
            isAd={false}
            quantity={1}
            digitalDataOrigin={digitalDataOrigin}
            digitalDataIndex={digitalIndex}
            primary
            showMiniBtn={showAddToCartButton}
          />
        </div>
      </div>
    </div>
  )
}
type ContainerProps = {
  children: React.ReactNode
  setShowAddTo: (arg: boolean) => void
  sizeClasses: Props['sizeClasses']
  digitalIndex: number
  product: Props['product']
  digitalDataOrigin: Props['digitalDataOrigin']
}

function pushViewItemList(
  digitalIndex?: number,
  digitalDataOrigin?: DigitalDataOrigin,
  product?: ProductWithCategoryType | ProductTypeProps | null
) {
  if (product && digitalDataOrigin && digitalIndex !== undefined) {
    ProductDigitalData.pushViewItemList(
      [
        {
          ...product,
          productListIndex: digitalIndex,
          label: getProductLabels(product as ProductTypeProps),
        },
      ] as ProductWithCategoryType[],
      digitalDataOrigin
    )
  }
}

const Container = ({
  children,
  setShowAddTo,
  sizeClasses,
  digitalIndex,
  product,
  digitalDataOrigin,
}: ContainerProps) => {
  const ref = useTrackingOnScreen(() => pushViewItemList(digitalIndex, digitalDataOrigin, product))

  const indexRef = useRef<HTMLDivElement | null>(null)
  const storedIndex = lastIndexVar()

  useEffect(() => {
    if (digitalIndex && storedIndex !== null && storedIndex === digitalIndex) {
      if (indexRef.current) {
        getRefVar(indexRef)
      }
    }
  }, [storedIndex])

  return (
    <div
      id={`compactCardContainer_${digitalIndex}`}
      ref={ref}
      className={classNames(
        sizeClasses,
        'c-product-card c-product-card--compact t-nullify u-overflow-hidden'
      )}
      onMouseEnter={() => setShowAddTo(true)}
      onMouseLeave={() => setShowAddTo(false)}>
      <div ref={indexRef}></div>
      {children}
    </div>
  )
}

export default ProductCardCompact
